import { Link, useNavigate } from 'react-router-dom'
import { products } from '../data/products'
import { services } from '../data/services'

const toolLinks = [
  { name: 'Bitmap Parser', href: '/tools/iso8583-bitmap' },
  { name: 'TLV Parser', href: '/tools/tlv' },
  { name: 'ISO Field Parser', href: 'https://app.accezzminds.com/login' },
  { name: 'Host Log Viewer', href: 'https://app.accezzminds.com/login' },
]

const companyLinks = [
  { name: 'About Us', href: '/#company' },
  { name: 'Contact', href: '/#contact' },
]

export default function Footer() {
  const navigate = useNavigate()

  const scrollTo = (href: string) => {
    if (href.startsWith('/#')) {
      const id = href.slice(2)
      if (window.location.pathname !== '/') {
        navigate('/')
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const renderLink = (name: string, href: string) => {
    if (href.startsWith('http')) {
      return (
        <a href={href} className="text-sm text-gray-400 hover:text-white transition-colors">
          {name}
        </a>
      )
    }
    if (href.startsWith('/') && !href.startsWith('/#')) {
      return (
        <Link to={href} className="text-sm text-gray-400 hover:text-white transition-colors">
          {name}
        </Link>
      )
    }
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault()
          scrollTo(href)
        }}
        className="text-sm text-gray-400 hover:text-white transition-colors"
      >
        {name}
      </a>
    )
  }

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[2fr_1fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Accezz Minds" className="h-10 w-auto brightness-200" />
              <span className="text-lg font-semibold text-white">Accezz Minds</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-6 text-gray-400">
              Future-ready payment solutions powered by smart cards and EMV technology.
            </p>
            <div className="mt-6 flex gap-4">
              <a
                href="mailto:info@accezzminds.com"
                className="rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                aria-label="Email"
              >
                <svg
                  className="size-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-white">Products</p>
            <ul className="mt-4 space-y-3">
              {products.map((p) => (
                <li key={p.id}>{renderLink(p.name, `/products/${p.id}`)}</li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-white">Services</p>
            <ul className="mt-4 space-y-3">
              {services.map((s) => (
                <li key={s.id}>{renderLink(s.name, `/services/${s.id}`)}</li>
              ))}
            </ul>
          </div>

          {/* Tools */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-white">Tools</p>
            <ul className="mt-4 space-y-3">
              {toolLinks.map((link) => (
                <li key={link.name}>{renderLink(link.name, link.href)}</li>
              ))}
            </ul>
            <Link
              to="/tools"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              More tools <span aria-hidden="true">→</span>
            </Link>
          </div>

          {/* Company */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-white">Company</p>
            <ul className="mt-4 space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>{renderLink(link.name, link.href)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-gray-800 pt-8">
          <p className="text-center text-sm text-gray-500">
            Level 11, Prestige Khoday Center, No 5 Rajbhavan Road, Bangalore – 560001, Karnataka,
            INDIA
          </p>
          <p className="mt-2 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Accezz Minds Technologies Pvt. Ltd. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
